import { EmployeeOverview } from './../../interface/config.interface';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import {
  DataComponentApplicationDetails,
  DocumentData,
  TechnicianApplicationData,
  TechnicianDetailsData,
  UpdateTechnicianApplication,
} from '../employee-overview.domain';
import { EmployeeOverviewService } from '../employee-overview.service';
import { FileData, FormatterData, UserStatus } from '../../constants/app.constants';
import { FileService } from '../../services/file.service';
import { LoggerService } from '../../services/logger.service';
import { CompanyData, DateFormat, IFileData, KeyValueData, TechnicianData } from '../../domains/common.domain';
import { CommonService } from '../../services/common.service';
import { ErrorService } from '../../services/error/error.service';
import { forkJoin } from 'rxjs';
import { EnvironmentService } from 'src/app/services/environment.service';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';

@Component({
  providers: [EmployeeOverviewService],
  selector: 'app-details-form',
  templateUrl: './details-form.component.html',
  styleUrls: ['./details-form.component.scss'],
})
export class DetailsFormComponent implements OnInit {
  private _detailsData: TechnicianDetailsData = {
    allowEdit: false,
    formData: null,
    isReadOnly: false,
    staticData: {
      leftPanel: [],
      rightPanel: [],
    },
    tabIndex: -1,
  };

  @Input() public set detailsData(value: TechnicianDetailsData) {
    const copyOfValue = JSON.parse(JSON.stringify(value));
    const documentTypes = (copyOfValue.formData.document || [])
      .map(d => d.name)
      .reduce((prev, curr) => {
        prev[curr] = true;
        return prev;
      }, {});

    copyOfValue.staticData.leftPanel = copyOfValue.staticData.leftPanel
      .map(section => {
        section.data = section.data.filter(doc => documentTypes[doc.name]);
        return section;
      })
      .filter(section => section.data?.length);

    this._detailsData = copyOfValue;
    this.documentsData = copyOfValue.formData.document || [];
  }
  public get detailsData(): TechnicianDetailsData {
    return this._detailsData;
  }

  @Output() public postApplicationUpdate: EventEmitter<{ index: number }>;

  companyData: CompanyData;
  dataObject: {
    isFormReadonly: boolean;
    technicianData: TechnicianApplicationData;
  };
  dataComponentFormInvalidState: boolean;
  dateFormat: DateFormat;
  detailsFormGroup: FormGroup;
  disableSubmitButton: boolean;
  documentsData: DocumentData[];
  errorMessage: string;
  isFormRejected: boolean;
  isLoading: boolean;
  isReadOnly: boolean;
  minDate: Date;
  technicianData: TechnicianData;
  fileData: IFileData;
  technicianRole: string;
  userStatus: KeyValueData;
  private technicianApplicationData: UpdateTechnicianApplication;

  constructor(
    private commonService: CommonService,
    private employeeOverviewService: EmployeeOverviewService,
    private errorService: ErrorService,
    private fileService: FileService,
    private environmentService: EnvironmentService,
    private translationService: NetzTranslateService
  ) {
    this.dateFormat = FormatterData.date;
    this.errorMessage = '';
    this.isFormRejected = false;
    this.isReadOnly = false;
    this.minDate = new Date();
    this.minDate.setDate(this.minDate.getDate() + 1);
    this.postApplicationUpdate = new EventEmitter<any>();
    this.fileData = FileData;
    this.userStatus = UserStatus;
  }

  get detailsSubmitButtonStatus(): boolean {
    return (this.detailsData.tabIndex === 3 && !this.detailsData.allowEdit) || this.detailsFormGroup.invalid;
  }

  ngOnInit(): void {
    this.dataComponentFormInvalidState = false;
    this.isLoading = true;

    this.companyData = (this.detailsData.formData.company && this.detailsData.formData.company[0]) || null;
    this.technicianData = (this.detailsData.formData.technician && this.detailsData.formData.technician[0]) || null;
    this.updateTechnicianDistrictHeating(this.technicianData);
    this.dataObject = {
      isFormReadonly: !this.detailsData.allowEdit,
      technicianData: this.detailsData.formData || null,
    };
    this.isReadOnly = this.detailsData.isReadOnly;
    this.technicianRole = this.technicianData?.role;

    if (this.technicianData) {
      if (this.technicianData.birthday) {
        this.technicianData.birthday = this.commonService.parseDateForUI(this.technicianData.birthday);
      }
      if (this.technicianData.concession_valid_until) {
        this.technicianData.concession_valid_until = this.commonService.parseDateForUI(this.technicianData.concession_valid_until);
      }
    }

    this.prepareForm();
    this.isLoading = false;

    this.detailsFormGroup.valueChanges.subscribe(() => {
      this.errorMessage = '';
      this.evaluateFormButtonState();
    });
  }

  checkForAvailableDocuments(name: string): boolean {
    return this.fileService.checkForEnabledAvailableDocuments(this.documentsData, name);
  }

  verifyForm(isFormApproved: boolean): void {
    this.isFormRejected = !isFormApproved;
    this.errorMessage = '';

    if (this.isFormRejected) {
      this.detailsFormGroup.controls.permissionUntil?.clearValidators();
      this.detailsFormGroup.controls.reasonDescription?.setValidators([Validators.required]);
    } else {
      this.detailsFormGroup.controls.reasonDescription?.clearValidators();
      this.detailsFormGroup.controls.permissionUntil?.setValidators([Validators.required]);
    }
    this.detailsFormGroup.controls.permissionUntil?.updateValueAndValidity();
    this.detailsFormGroup.controls.reasonDescription?.updateValueAndValidity();
  }

  updateFormData(): void {
    this.isLoading = true;
    const currentFormData = this.detailsFormGroup.getRawValue();
    if (currentFormData.birthday) {
      currentFormData.birthday = this.commonService.parseDateForSaving(currentFormData.birthday);
    }
    if (currentFormData.permissionUntil) {
      currentFormData.permissionUntil = this.commonService.parseDateForSaving(currentFormData.permissionUntil);
    }

    if (this.detailsFormGroup.valid) {
      if (this.isFormRejected) {
        const requestData = {
          technician_application: [
            {
              status: UserStatus.REVOKED,
              reason_description: currentFormData.reasonDescription,
            },
          ],
          notify_on_reject: !this.environmentService.dontNotifyTechnicianOnAppplicationReject,
        };

        this.employeeOverviewService.updateTechnicianApplicationStatus(`ta_id=${this.detailsData.formData.ta_id}`, requestData).subscribe(
          response => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(response, true);
            this.postApplicationUpdate.emit({ index: this.detailsData.tabIndex });
          },
          error => {
            this.isLoading = false;
            this.errorMessage = (error && error.message) || error;
            LoggerService.error('[DetailsFormComponent] Update Form Data', error);
          }
        );
      } else {
        const technicianRequestData: TechnicianData = {
          concession_water: currentFormData.water ? '1' : '0',
          concession_power: currentFormData.power ? '1' : '0',
          concession_gas: currentFormData.gas ? '1' : '0',
          concession_district_heating: currentFormData.heating ? '1' : '0',
          concession_valid_until: currentFormData.permissionUntil,
          status: UserStatus.ACTIVE,
          concessions_expired: '0',
        };

        const updateSubscription = this.employeeOverviewService.updateTechnicianDetails(
          this.technicianData.technician_id,
          technicianRequestData
        );

        updateSubscription.subscribe(
          () => {
            const applicationRequestData = {
              technician_application: [
                {
                  status:
                    this.technicianData?.['technician_application']?.status === UserStatus.PARTIALLY_ISSUED
                      ? UserStatus.PARTIALLY_ISSUED
                      : UserStatus.GRANTED,
                },
              ],
            };

            this.employeeOverviewService
              .updateTechnicianApplicationStatus(`ta_id=${this.detailsData.formData.ta_id}`, applicationRequestData)
              .subscribe(
                () => {
                  this.isLoading = false;
                  this.postApplicationUpdate.emit({ index: this.detailsData.tabIndex });
                },
                error => {
                  this.isLoading = false;
                  this.errorMessage = (error && error.message) || error;
                  console.log(error);
                }
              );
          },
          error => {
            this.errorMessage = (error && error.message) || error;
            console.log(error);
          }
        );
      }
    }
  }

  resetFormData() {
    this.detailsFormGroup.reset();
    this.prepareForm();

    this.dataObject = {
      isFormReadonly: !this.detailsData.allowEdit,
      technicianData: this.detailsData.formData || null,
    };
  }

  downloadFile(name: string): void {
    const object = this.getDimensionsByFilter(name);
    if (object !== null && object !== undefined && object.length > 0) {
      this.downloadSelectedFile(object[0].document_id, object[0].filename);
    }
  }

  getDimensionsByFilter(name: string): DocumentData[] {
    return this.fileService.getDimensionsByFilter(this.detailsData.formData.document, name);
  }

  downloadSelectedFile(documentId: string, fileName?: string): void {
    this.fileService.readDocument(documentId).subscribe((response: Blob) => {
      this.fileService.downloadDocument(response, fileName);
    });
  }

  verifyFilename(name: string): boolean {
    let isFileNamePresent = true;

    Object.keys(this.fileData).forEach(key => {
      isFileNamePresent = isFileNamePresent && name !== this.fileData[key];
    });

    return isFileNamePresent;
  }

  updateApplicationData(data: DataComponentApplicationDetails) {
    this.technicianApplicationData = data.applicationData;
    this.dataComponentFormInvalidState = data.formInvalidState;
    this.evaluateFormButtonState();
  }

  saveTechnicianApplicationData() {
    this.isLoading = true;

    const currentFormData = this.detailsFormGroup.value;
    this.technicianApplicationData.technician.concession_district_heating = currentFormData.heating ? '1' : '0';
    this.technicianApplicationData.technician.concession_gas = currentFormData.gas ? '1' : '0';
    this.technicianApplicationData.technician.concession_power = currentFormData.power ? '1' : '0';
    this.technicianApplicationData.technician.concession_water = currentFormData.water ? '1' : '0';
    if (this.detailsData.tabIndex === 3) {
      this.technicianApplicationData.technician.role = currentFormData.role;
      this.technicianApplicationData.technician.status = 'active';
    }
    const technicianDataSubscription = this.employeeOverviewService.updateTechnicianDetails(
      this.technicianApplicationData.technician.technician_id,
      this.technicianApplicationData.technician
    );
    const companyDataSubscription = this.employeeOverviewService.updateCompanyData(
      this.technicianApplicationData.company.company_id,
      this.technicianApplicationData.company
    );

    forkJoin([technicianDataSubscription, companyDataSubscription]).subscribe(
      response => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        if (response && response.length === 2) {
          this.postApplicationUpdate.emit({ index: this.detailsData.tabIndex });
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  getOtherDocumentLabel(documentName: string): string {
    const labelKey = `basic-profile.fields.${documentName}`;
    const translation = this.translationService.translateString(labelKey);
    const hasTranslation = translation != labelKey;

    return hasTranslation ? translation : documentName;
  }

  private prepareForm() {
    this.detailsFormGroup = new FormGroup({
      heating: new FormControl({
        value: (this.technicianData && this.technicianData.concession_district_heating === '1') || false,
        disabled: !this.detailsData.allowEdit,
      }),
      gas: new FormControl({
        value: (this.technicianData && this.technicianData.concession_gas === '1') || false,
        disabled: !this.detailsData.allowEdit,
      }),
      power: new FormControl({
        value: (this.technicianData && this.technicianData.concession_power === '1') || false,
        disabled: !this.detailsData.allowEdit,
      }),
      water: new FormControl({
        value: (this.technicianData && this.technicianData.concession_water === '1') || false,
        disabled: !this.detailsData.allowEdit,
      }),
      role: new FormControl({
        value: this.technicianData && this.technicianData.role ? this.technicianData.role : '',
        disabled: !this.detailsData.allowEdit,
      }),
    });

    if (!this.detailsData.allowEdit || (this.detailsData.allowEdit && !this.isReadOnly)) {
      this.detailsFormGroup.addControl(
        'permissionUntil',
        new FormControl(
          {
            value: this.technicianData?.concession_valid_until || '',
            disabled: this.detailsData.tabIndex === 3 && !this.detailsData.allowEdit,
          },
          [Validators.required]
        )
      );
      this.detailsFormGroup.addControl(
        'reasonDescription',
        new FormControl({
          value: this.detailsData?.formData.reasonDescription || '',
          disabled: this.detailsData.tabIndex === 3 && !this.detailsData.allowEdit,
        })
      );
      this.detailsFormGroup.addControl(
        'registeredNetworkOperator',
        new FormControl({
          value: this.companyData?.registered_network_operator || '',
          disabled: this.detailsData.tabIndex === 3 && !this.detailsData.allowEdit,
        })
      );

      if (this.detailsData.formData.reasonDescription) {
        this.verifyForm(false);
      }
    }
  }

  private evaluateFormButtonState() {
    this.disableSubmitButton = this.detailsData.allowEdit
      ? this.detailsFormGroup.invalid ||
        !(
          this.detailsFormGroup.value.heating ||
          this.detailsFormGroup.value.gas ||
          this.detailsFormGroup.value.power ||
          this.detailsFormGroup.value.water
        ) ||
        this.dataComponentFormInvalidState
      : this.detailsFormGroup.invalid;
  }

  //When the technician application status is partially issued, district heating should be approved manually by employee and the other commodities are approved automatically by API.
  private updateTechnicianDistrictHeating(technician: TechnicianData): void {
    if (technician?.['technician_application']?.status === UserStatus.PARTIALLY_ISSUED) {
      technician.concession_district_heating = '1';
    }
  }
}
